import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { createEditor, Editor } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import isHotkey from 'is-hotkey';
import { MARK_BOLD, MARK_ITALIC, MARK_UNDERLINE, ELEMENT_IMAGE, ELEMENT_LINK } from "./platePlugins";
import api from "../../services/api";
import { MEDIA_LIMITS } from "../../config/api.config";
import "./PostForm.css";
import ImageButton from "../ImageButton";
import LinkButton from "../LinkButton";
import CoverImageDialog from "../CoverImageDialog";
import LinkElement from "./LinkElement";
import { ImageElement } from "./ImageElement";
import "./ImageElement.css";

// 將連結標記為 inline，避免被視為區塊元素
const withLinks = (editor) => {
  const { isInline } = editor;

  editor.isInline = (element) => {
    if (element.type === ELEMENT_LINK) {
      return true;
    }
    return isInline(element);
  };

  return editor;
};

// 修復問題 A: 創建 withImages 插件來正確設置 void elements
const withImages = (editor) => {
  const { isVoid } = editor;

  editor.isVoid = (element) => {
    return element.type === ELEMENT_IMAGE ? true : isVoid(element);
  };

  return editor;
};

const createEmptySlateValue = () => ([
  {
    type: "p",
    children: [{ text: "" }],
  },
]);

const cloneSlateValue = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    console.warn("無法複製 Slate 內容:", error);
    return createEmptySlateValue();
  }
};

const resolvedMaxImageSizeMb = Number.isFinite(MEDIA_LIMITS.POST_IMAGE_MAX_MB) && MEDIA_LIMITS.POST_IMAGE_MAX_MB > 0
  ? MEDIA_LIMITS.POST_IMAGE_MAX_MB
  : 15;
const MAX_IMAGE_SIZE_MB = resolvedMaxImageSizeMb;
const MAX_IMAGE_SIZE = MAX_IMAGE_SIZE_MB * 1024 * 1024;

const useImagePreview = (imageFile) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  return previewUrl;
};

// 工具函數：內容有意義檢查
const hasMeaningfulContent = (content) => {
  if (!Array.isArray(content)) return false;

  for (const node of content) {
    if (!node) {
      continue;
    }
    if (node.type === 'paragraph' || node.type === 'p') {
      const text = node.children?.map(child => child.text || '').join('').trim();
      if (text && text.length > 0) return true;
    } else if (node.type === 'image' || node.type === 'link') {
      return true; // 圖片或連結視為有意義的內容
    }
  }
  return false;
};

const extractPlainText = (nodes) => {
  if (!Array.isArray(nodes)) return "";
  return nodes
    .map((node) => {
      if (!node) {
        return "";
      }
      if (typeof node.text === "string") {
        return node.text;
      }
      if (Array.isArray(node.children)) {
        return extractPlainText(node.children);
      }
      return "";
    })
    .join("");
};

// 檢查標記是否活動的函數
const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// Render functions for Slate
const renderLeaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  return <span {...attributes}>{children}</span>;
};

// 1. 抽出 useTopics hook
const useTopics = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTopics = async () => {
      setLoading(true);
      try {
        const response = await api.get("topics/");
        const topicsData = Array.isArray(response.data) ? response.data : [];
        setTopics(topicsData);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch topics:", err);
        setTopics([]);
        setError("載入主題失敗");
      } finally {
        setLoading(false);
      }
    };
    fetchTopics();
  }, []);

  return { topics, loading, error };
};

const PostForm = ({ onPostSubmit, bareLayout = false }) => {
  const [title, setTitle] = useState("");
  const [richContent, setRichContent] = useState(() => createEmptySlateValue());
  const { topics, loading: topicsLoading, error: topicsError } = useTopics();
  const [selectedTopic, setSelectedTopic] = useState("");
  const [mainImage, setMainImage] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isEditorExpanded, setIsEditorExpanded] = useState(false);
  const expandButtonRef = useRef(null);
  const shouldRestoreFocusRef = useRef(false);
  const [isCoverDialogOpen, setIsCoverDialogOpen] = useState(false);
  const [pendingCoverFile, setPendingCoverFile] = useState(null);
  const coverFileInputRef = useRef(null);

  const previewImageUrl = useImagePreview(mainImage);
  const steps = [
    { id: "basic", label: "基本設定" },
    { id: "content", label: "撰寫內容" },
    { id: "media", label: "封面媒體" },
    { id: "preview", label: "預覽確認" },
  ];
  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const selectedTopicName = useMemo(() => {
    const topic = topics.find((item) => String(item.id) === String(selectedTopic));
    return topic ? topic.name : "";
  }, [topics, selectedTopic]);
  const previewText = extractPlainText(richContent).replace(/\s+/g, " ").trim();
  const truncatedPreviewText = previewText.length > 280 ? `${previewText.slice(0, 280)}…` : previewText;
  const navigate = useNavigate();

  const validateStep = (stepId) => {
    switch (stepId) {
      case "basic":
        if (!title.trim()) {
          setError("請先填寫標題。");
          return false;
        }
        if (!selectedTopic) {
          setError("請先選擇主題。");
          return false;
        }
        break;
      case "content":
        if (!hasMeaningfulContent(richContent)) {
          setError("內容不能為空，請輸入文字或插入圖片/連結。");
          return false;
        }
        break;
      case "media":
        if (!mainImage) {
          setError("請上傳封面圖片。");
          return false;
        }
        break;
      case "preview":
        if (!title.trim()) {
          setError("請先填寫標題。");
          return false;
        }
        if (!selectedTopic) {
          setError("請先選擇主題。");
          return false;
        }
        if (!hasMeaningfulContent(richContent)) {
          setError("請確認已填寫內文。");
          return false;
        }
        if (!mainImage) {
          setError("請加入封面圖片。");
          return false;
        }
        break;
      default:
        break;
    }
    setError("");
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep.id)) {
      setCurrentStepIndex((index) => Math.min(index + 1, steps.length - 1));
      scrollToTop();
    }
  };

  const handleBack = () => {
    setError("");
    setCurrentStepIndex((index) => Math.max(index - 1, 0));
    scrollToTop();
  };

  // Initialize Slate editor with withImages plugin
  const editor = useMemo(
    () => withLinks(withImages(withHistory(withReact(createEditor())))),
    []
  );

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const keydownHandler = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsEditorExpanded(false);
      }
    };

    if (isEditorExpanded) {
      document.body.classList.add("pf-scroll-locked");
      document.addEventListener("keydown", keydownHandler);
    } else {
      document.body.classList.remove("pf-scroll-locked");
    }

    return () => {
      document.body.classList.remove("pf-scroll-locked");
      document.removeEventListener("keydown", keydownHandler);
    };
  }, [isEditorExpanded]);

  useEffect(() => {
    if (!isEditorExpanded) {
      return;
    }

    const focusTimer = setTimeout(() => {
      try {
        ReactEditor.focus(editor);
      } catch (focusError) {
        console.warn("無法將焦點設置到編輯器:", focusError);
      }
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [editor, isEditorExpanded]);

  useEffect(() => {
    if (!isEditorExpanded && shouldRestoreFocusRef.current) {
      shouldRestoreFocusRef.current = false;
      if (expandButtonRef.current) {
        expandButtonRef.current.focus();
      }
    }
  }, [isEditorExpanded]);

  const openEditorModal = () => {
    shouldRestoreFocusRef.current = false;
    setIsEditorExpanded(true);
  };
  const closeEditorModal = () => {
    shouldRestoreFocusRef.current = true;
    setIsEditorExpanded(false);
  };

  const handleMainImageUpload = (e) => {
    // 防護檢查：確保有 files 且 files 有內容
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("僅支援上傳 JPG、PNG 或 WebP 圖片格式。");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setError(`圖片大小超過 ${MAX_IMAGE_SIZE_MB}MB 限制，請先壓縮後再試。`);
      e.target.value = "";
      return;
    }

    setError("");
    setPendingCoverFile(file);
    setIsCoverDialogOpen(true);
    if (coverFileInputRef.current) {
      coverFileInputRef.current.value = "";
    } else {
      e.target.value = "";
    }
  };

  const clearMainImage = () => {
    setMainImage(null);
    setError("");
  };

  const handleCoverDialogClose = () => {
    setIsCoverDialogOpen(false);
    setPendingCoverFile(null);
  };

  const handleCoverDialogComplete = ({ file }) => {
    setMainImage(file);
    setIsCoverDialogOpen(false);
    setPendingCoverFile(null);
    setError("");
  };

  const handleRecropMainImage = () => {
    if (!mainImage) return;
    setPendingCoverFile(mainImage);
    setIsCoverDialogOpen(true);
  };

  // 送出時根據步驟整理資料，交由父層處理
  const handleSubmit = async () => {
    if (!validateStep("preview")) {
      setCurrentStepIndex(steps.length - 1);
      return;
    }

    setError("");

    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("請先登入後再發佈貼文！");
      return;
    }

    const selectedTopicId = Number(selectedTopic);
    const selectedTopicObj = topics.find((topic) => topic.id === selectedTopicId);
    if (!selectedTopicObj) {
      setError("請選擇有效的主題！");
      setCurrentStepIndex(0);
      return;
    }

    if (!mainImage) {
      setError("請上傳封面圖片！");
      setCurrentStepIndex(2);
      return;
    }

    if (mainImage.size > MAX_IMAGE_SIZE) {
      setError(`圖片大小超過 ${MAX_IMAGE_SIZE_MB}MB 限制，請壓縮後再試。`);
      setCurrentStepIndex(2);
      return;
    }

    const postContent = JSON.stringify(richContent);
    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("content", postContent);
    formData.append("topic", String(selectedTopicObj.id));
    if (mainImage) {
      formData.append("image", mainImage);
    }

    setIsSubmitting(true);
    try {
      if (typeof onPostSubmit !== "function") {
        throw new Error("onPostSubmit callback is not provided.");
      }

      const maybePromise = onPostSubmit(formData, {
        title: title.trim(),
        topicId: selectedTopicObj.id,
        topicName: selectedTopicObj.name,
        richContent,
      });

      let result = maybePromise;
      if (maybePromise && typeof maybePromise.then === "function") {
        result = await maybePromise;
      }

      if (result && (result.uuid || result.id)) {
        navigate(`/posts/${result.uuid || result.id}`);
        return;
      }

      setTitle("");
      setRichContent(createEmptySlateValue());
      setSelectedTopic("");
      setMainImage(null);
      setCurrentStepIndex(0);
      setError("");
      scrollToTop();
    } catch (submissionError) {
      const message = submissionError?.message || "發佈失敗，請稍後再試。";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 1. 移除重複的 renderElement - 只保留一個統一版本
  const renderElement = ({ attributes, children, element }) => {
    switch (element.type) {
      case ELEMENT_IMAGE: // 統一使用常數
        return <ImageElement attributes={attributes} children={children} element={element} />;
      case ELEMENT_LINK: // 統一使用常數
        return <LinkElement attributes={attributes} children={children} element={element} />;
      case 'paragraph':
      case 'p':
      default:
        return <p {...attributes}>{children}</p>;
    }
  };

  const getStepSections = () => {
    switch (currentStep.id) {
      case "basic":
        return {
          aside: (
            <div className="step-support-card">
              <h3>旅人小提醒</h3>
              <p>先設定標題與主題，讓社群快速理解您的夜晚故事。</p>
              <ul className="step-support-list">
                <li>標題不必太長，點出亮點即可。</li>
                <li>選擇最貼近的主題，提升被看見的機率。</li>
              </ul>
            </div>
          ),
          main: (
            <>
              <div className="form-section">
                <label htmlFor="post-title">標題</label>
                <input
                  id="post-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="請輸入貼文標題"
                  required
                />
              </div>
              <div className="form-section">
                <label htmlFor="post-topic">主題</label>
                <select
                  id="post-topic"
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  disabled={topicsLoading || !topics.length}
                  required
                >
                  <option value="" disabled>
                    {topicsLoading ? "主題載入中..." : "請選擇主題"}
                  </option>
                  {Array.isArray(topics) && topics.map((topic) => (
                    <option key={topic.id} value={String(topic.id)}>
                      {topic.name}
                    </option>
                  ))}
                </select>
                <p className={`form-helper-text ${topicsError ? "error" : ""}`}>
                  {topicsError || "選擇最貼近的主題，方便旅人找到您的文章。"}
                </p>
              </div>
            </>
          ),
        };
      case "content":
        return {
          aside: (
            <div className="step-support-card">
              <h3>編輯小技巧</h3>
              <ul className="step-support-list">
                <li>使用 <span className="kbd">⌘/Ctrl</span> + <span className="kbd">B</span> 套用粗體。</li>
                <li>透過工具列插入圖片與連結，增添故事層次。</li>
                <li>善用段落與空白，讓行程脈絡更清晰。</li>
              </ul>
            </div>
          ),
          main: (
            <div className="form-section form-section-editor">
              {(() => {
                const portalTarget = typeof document !== "undefined" ? document.body : null;

                const shell = (
                  <div className={`plate-editor-shell ${isEditorExpanded ? "plate-editor-shell--expanded" : ""}`}>
                    {isEditorExpanded && (
                      <button
                        type="button"
                        className="plate-editor-backdrop"
                        aria-label="關閉內容編輯視窗"
                        onClick={closeEditorModal}
                      />
                    )}
                    <div
                      className={`plate-editor ${isEditorExpanded ? "plate-editor--expanded" : ""}`}
                      role={isEditorExpanded ? "dialog" : "group"}
                      aria-modal={isEditorExpanded || undefined}
                      aria-labelledby="post-content-editor-label"
                    >
                      <Slate
                        editor={editor}
                        value={richContent}
                        initialValue={richContent}
                        onChange={(value) => {
                          if (!isComposing && Array.isArray(value)) setRichContent(cloneSlateValue(value));
                        }}
                      >
                        <div className="plate-editor-header">
                          <div className="plate-editor-header-main">
                            <span id="post-content-editor-label" className="plate-editor-label">
                              內容
                            </span>
                            {!isEditorExpanded && (
                              <button
                                type="button"
                                className="plate-editor-expand"
                                onClick={openEditorModal}
                                aria-label="展開內容編輯視窗"
                                ref={expandButtonRef}
                              >
                                展開撰寫
                              </button>
                            )}
                          </div>
                          <div className="plate-toolbar">
                            <button
                              className={`toolbar-button ${isMarkActive(editor, MARK_BOLD) ? "active" : ""}`}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                toggleMark(editor, MARK_BOLD);
                              }}
                              type="button"
                              aria-label="粗體"
                              disabled={!isEditorExpanded}
                            >
                              <strong>B</strong>
                            </button>
                            <button
                              className={`toolbar-button ${isMarkActive(editor, MARK_ITALIC) ? "active" : ""}`}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                toggleMark(editor, MARK_ITALIC);
                              }}
                              type="button"
                              aria-label="斜體"
                              disabled={!isEditorExpanded}
                            >
                              <em>I</em>
                            </button>
                            <button
                              className={`toolbar-button ${isMarkActive(editor, MARK_UNDERLINE) ? "active" : ""}`}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                toggleMark(editor, MARK_UNDERLINE);
                              }}
                              type="button"
                              aria-label="底線"
                              disabled={!isEditorExpanded}
                            >
                              <u>U</u>
                            </button>
                            <ImageButton className="toolbar-button" disabled={!isEditorExpanded} />
                            <LinkButton className="toolbar-button" disabled={!isEditorExpanded} />
                          </div>
                          {isEditorExpanded && (
                            <button
                              type="button"
                              className="plate-editor-close"
                              onClick={closeEditorModal}
                              aria-label="關閉內容編輯視窗"
                            >
                              關閉
                            </button>
                          )}
                        </div>
                        <Editable
                          id="post-content-editor"
                          aria-labelledby="post-content-editor-label"
                          aria-label={!isEditorExpanded ? "展開撰寫" : undefined}
                          placeholder="請輸入文章內容..."
                          spellCheck={true}
                          className={`plate-editor-editable ${isEditorExpanded ? "plate-editor-editable--active" : ""}`.trim()}
                          style={{ position: "relative", whiteSpace: "pre-wrap", overflowWrap: "break-word" }}
                          readOnly={!isEditorExpanded}
                          tabIndex={isEditorExpanded ? undefined : 0}
                          onClick={(event) => {
                            if (!isEditorExpanded) {
                              event.preventDefault();
                              openEditorModal();
                            }
                          }}
                          onFocus={() => {
                            if (!isEditorExpanded) {
                              openEditorModal();
                            }
                          }}
                          onCompositionStart={() => setIsComposing(true)}
                          onCompositionEnd={() => {
                            setIsComposing(false);
                            setTimeout(() => {
                              const value = editor.children;
                              if (Array.isArray(value)) setRichContent(cloneSlateValue(value));
                            }, 0);
                          }}
                          onKeyDown={(event) => {
                            if (!isEditorExpanded) {
                              event.preventDefault();
                              openEditorModal();
                              return;
                            }

                            if (isComposing) return;

                            if (isHotkey("mod+b", event)) {
                              event.preventDefault();
                              toggleMark(editor, MARK_BOLD);
                            }
                            if (isHotkey("mod+i", event)) {
                              event.preventDefault();
                              toggleMark(editor, MARK_ITALIC);
                            }
                            if (isHotkey("mod+u", event)) {
                              event.preventDefault();
                              toggleMark(editor, MARK_UNDERLINE);
                            }
                          }}
                          renderElement={renderElement}
                          renderLeaf={renderLeaf}
                        />
                      </Slate>
                    </div>
                  </div>
                );

                if (isEditorExpanded && portalTarget) {
                  return createPortal(shell, portalTarget);
                }

                return shell;
              })()}
            </div>
          ),
        };
      case "media":
        return {
          aside: (
            <div className="upload-guidelines">
              <span className="guideline-title">媒體建議</span>
              <ul>
                <li>建議尺寸 1280×720 (16:9)，亮點置中更安全。</li>
                <li>避免出現敏感資訊或可辨識證件。</li>
                <li>上傳後仍可返回前一步調整。</li>
              </ul>
            </div>
          ),
          main: (
            <>
              <div className="form-section image-upload-section">
                <label htmlFor="main-image-upload">封面圖片</label>
                {mainImage ? (
                  <div className="image-preview-wrapper">
                    {previewImageUrl ? (
                      <>
                        <img src={previewImageUrl} alt="封面預覽" />
                        <div className="preview-crop-frame" aria-hidden="true" />
                      </>
                    ) : (
                      <div className="image-preview-placeholder">封面預覽載入中...</div>
                    )}
                    <div className="image-preview-actions">
                      <button
                        type="button"
                        className="image-preview-action"
                        onClick={handleRecropMainImage}
                      >
                        重新裁切
                      </button>
                      <button
                        className="remove-image-button"
                        type="button"
                        onClick={clearMainImage}
                        title="移除圖片"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="white"
                        >
                          <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12Z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <label htmlFor="main-image-upload" className="upload-button">
                      <div className="main-image-upload-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24"
                          width="24"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M5 20q-.825 0-1.413-.588Q3 18.825 3 18V6q0-.825.587-1.413Q4.175 4 5 4h4.15l1.75-2h2.2l1.75 2H19q.825 0 1.413.587Q21 5.175 21 6v12q0 .825-.587 1.412Q19.825 20 19 20Zm7-3.5q1.875 0 3.188-1.312Q16.5 13.875 16.5 12t-1.312-3.188Q13.875 7.5 12 7.5t-3.188 1.312Q7.5 10.125 7.5 12t1.312 3.188Q10.125 16.5 12 16.5Z" />
                        </svg>
                        <span>上傳圖片</span>
                      </div>
                    </label>
                    <input
                      id="main-image-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      ref={coverFileInputRef}
                      onChange={handleMainImageUpload}
                    />
                  </div>
                )}
              </div>
              <p className="form-helper-text">
                建議避免過亮或過暗的照片，封面會影響列表吸睛程度。
              </p>
            </>
          ),
        };
      case "preview":
      default:
        return {
          aside: (
            <div className="preview-cover">
              {previewImageUrl ? (
                <img src={previewImageUrl} alt="封面預覽" />
              ) : (
                <div className="preview-cover placeholder">尚未上傳封面</div>
              )}
            </div>
          ),
          main: (
            <div className="preview-card">
              <h3>發佈前檢查</h3>
              <dl className="preview-summary">
                <div>
                  <dt>標題</dt>
                  <dd>{title || "尚未填寫"}</dd>
                </div>
                <div>
                  <dt>主題</dt>
                  <dd>{selectedTopicName || "尚未選擇"}</dd>
                </div>
              </dl>
              <div className="preview-content">
                <h4>內文預覽</h4>
                <p>{truncatedPreviewText || "尚未輸入內容"}</p>
              </div>
            </div>
          ),
        };
    }
  };

  const stepSections = getStepSections();
  const formLayoutClass = bareLayout ? "form-layout form-layout--bare" : "form-layout";
  const layoutClassNames = stepSections.aside
    ? formLayoutClass
    : `${formLayoutClass} form-layout--single`;
  const formFieldsColumnClass = stepSections.aside
    ? "form-fields-column"
    : "form-fields-column form-fields-column--full";
  const wizardClassName = bareLayout ? "form-wizard form-wizard--bare" : "form-wizard";

  const formContent = (
    <div className={wizardClassName}>
      <div className="form-stepper">
        {steps.map((step, index) => {
          const statusClass = index === currentStepIndex ? "active" : index < currentStepIndex ? "completed" : "";
          return (
            <div key={step.id} className={`form-step ${statusClass}`}>
              <span className="form-step-index">{index + 1}</span>
              <span className="form-step-label">{step.label}</span>
            </div>
          );
        })}
      </div>
      <div className={layoutClassNames}>
        {stepSections.aside && (
          <div className="form-media-column">
            {stepSections.aside}
          </div>
        )}
        <div className={formFieldsColumnClass}>
          {error && <p className="error-message">{error}</p>}
          {stepSections.main}
          <div className="form-actions">
            {currentStepIndex > 0 && (
              <button
                type="button"
                className="secondary"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                上一步
              </button>
            )}
            {!isLastStep ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={isSubmitting || (currentStep.id === "basic" && (topicsLoading || !!topicsError))}
              >
                下一步
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "發佈中..." : "📤 發佈"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (bareLayout) {
    return (
      <>
        {formContent}
        <CoverImageDialog
          isOpen={isCoverDialogOpen}
          initialFile={pendingCoverFile}
          onClose={handleCoverDialogClose}
          onComplete={handleCoverDialogComplete}
        />
      </>
    );
  }

  return (
    <section className="form-container">
      {formContent}
      <CoverImageDialog
        isOpen={isCoverDialogOpen}
        initialFile={pendingCoverFile}
        onClose={handleCoverDialogClose}
        onComplete={handleCoverDialogComplete}
      />
    </section>
  );
};

export default PostForm;
